import React, { useCallback, useState } from "react";
import ContactBlockAlert from "components/ContactBlockAlert";
import {
  CONTACT_BLOCK_COPY,
  applyContactBlockFormikState,
  applyNumericContactFieldChange,
  getActiveContactBlockReason,
  getContactFieldClassName,
  getUnmarkFlags,
  getUnmarkLabel,
  getUnmarkWrongContacts,
  isContactSaveBlocked,
  resolveContactFieldValue,
} from "lookup/ContactBlockConstants";

const useContactBlock = ({
  allowUnmark = true,
  forceBlacklistedReason = false,
  previouslyWrongSuffix,
  enabled = true,
} = {}) => {
  const [contactBlockSnapshot, setContactBlockSnapshot] = useState(null);
  const [unmarkAck, setUnmarkAck] = useState({});

  const resetContactBlock = useCallback(() => {
    setContactBlockSnapshot(null);
    setUnmarkAck({});
  }, []);

  const applyIfContactBlocked = useCallback(
    (response, values, setErrors, setTouched, setSubmitting) => {
      const snapshot = applyContactBlockFormikState({
        response,
        values,
        setErrors,
        setTouched,
        setSubmitting,
      });
      if (!snapshot) {
        return false;
      }
      setContactBlockSnapshot(snapshot);
      setUnmarkAck({});
      return true;
    },
    []
  );

  const getUnmarkFlagsForValues = (values) =>
    getUnmarkFlags(contactBlockSnapshot, values, unmarkAck);

  const isSubmitBlocked = (values) =>
    enabled &&
    isContactSaveBlocked({
      snapshot: contactBlockSnapshot,
      values,
      unmarkAck: allowUnmark ? unmarkAck : {},
    });

  const getFormHelpers = ({
    values,
    errors,
    touched,
    setFieldValue,
    setErrors,
    onNumberChange = applyNumericContactFieldChange,
  }) => {
    const getActiveBlockReason = (fieldName) =>
      getActiveContactBlockReason({
        snapshot: contactBlockSnapshot,
        fieldName,
        currentValue: values[fieldName],
        enabled,
        forceBlacklistedReason,
      });

    const onBlockedContactFieldChange = (eventOrValue, fieldName) => {
      onNumberChange(
        resolveContactFieldValue(eventOrValue),
        fieldName,
        setFieldValue
      );
      if (
        contactBlockSnapshot?.fields?.includes(fieldName) &&
        typeof setErrors === "function"
      ) {
        const next = { ...errors };
        delete next[fieldName];
        setErrors(next);
      }
      if (allowUnmark && unmarkAck[fieldName]) {
        setUnmarkAck((prev) => ({ ...prev, [fieldName]: false }));
      }
    };

    const renderContactBlockAlert = (fieldName) => {
      const reason = getActiveBlockReason(fieldName);
      if (!reason) {
        return null;
      }
      return React.createElement(ContactBlockAlert, {
        fieldName,
        value: values[fieldName],
        reason,
        message:
          contactBlockSnapshot?.messages?.[fieldName] ||
          CONTACT_BLOCK_COPY.blacklisted,
        previouslyWrongSuffix,
        unmarkLabel: getUnmarkLabel(fieldName),
        showUnmark: allowUnmark,
        acknowledged: !!unmarkAck[fieldName],
        onAcknowledgeChange: (checked) =>
          setUnmarkAck((prev) => ({ ...prev, [fieldName]: checked })),
      });
    };

    const contactFieldClassName = (fieldName) =>
      getContactFieldClassName(getActiveBlockReason(fieldName));

    const showInlineFieldError = (fieldName) =>
      errors[fieldName] &&
      touched[fieldName] &&
      !getActiveBlockReason(fieldName);

    return {
      contactSubmitBlocked: isSubmitBlocked(values),
      unmarkWrongContacts: getUnmarkWrongContacts(
        contactBlockSnapshot,
        values,
        unmarkAck
      ),
      getActiveBlockReason,
      onBlockedContactFieldChange,
      renderContactBlockAlert,
      contactFieldClassName,
      showInlineFieldError,
    };
  };

  return {
    contactBlockSnapshot,
    unmarkAck,
    resetContactBlock,
    applyIfContactBlocked,
    getUnmarkFlagsForValues,
    isSubmitBlocked,
    getFormHelpers,
  };
};

export default useContactBlock;
